import React, { useRef, useState, useEffect } from 'react';
import { Button, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { Modal, ModalHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { addNewWorkOrder, updateWorkOrder } from '../../../../slices/workOrder/thunk';
import { setRequireCloseModal } from '../../../../slices/workOrder/reducer';
import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';
import { getWorkOrderApi } from '../../../../helpers/work_order/api_helper';
import WorkForm from '../Forms/WorkForm';

const AddWorkModal = ({ isOpen, handleToggleModal, waterRightData, dataToEdit, toggleRefetch }) => {
	const dispatch = useDispatch();
	const formRef = useRef(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fullRecord, setFullRecord] = useState(null);
	const [isLoadingRecord, setIsLoadingRecord] = useState(false);

	const isEditMode = !!dataToEdit;

	const selectorWorkOrder = createSelector(
		state => state.WorkOrder,
		data => ({
			isAdding: data.isAdding,
			isUpdating: data.isUpdating,
			requireCloseModal: data.requireCloseModal,
		})
	);

	const { isAdding, isUpdating, requireCloseModal } = useSelector(selectorWorkOrder);

	useEffect(() => {
		if (isOpen && dataToEdit?._id) {
			setIsLoadingRecord(true);
			getWorkOrderApi(dataToEdit._id)
				.then(response => {
					const record = response.data || response;
					if (record.reporting_start_date) {
						record.reporting_start_date = new Date(record.reporting_start_date);
					}
					setFullRecord(record);
				})
				.catch(err => console.error('Error al cargar obra:', err))
				.finally(() => setIsLoadingRecord(false));
		} else if (!isOpen) {
			setFullRecord(null);
		}
	}, [isOpen, dataToEdit?._id]);

	useEffect(() => {
		if (isOpen && requireCloseModal) {
			dispatch(setRequireCloseModal(false));
			handleToggleModal();
			if (toggleRefetch) {
				setTimeout(() => toggleRefetch(), 100);
			}
		}
	}, [requireCloseModal]);

	const handleSubmit = async values => {
		setIsSubmitting(true);
		try {
			let file_id = fullRecord?.file_id || dataToEdit?.file_id || '69a65cad2ab27416dbe53568';

			if (values.work_proof instanceof File) {
				const formData = new FormData();
				formData.append('files', values.work_proof);
				const uploadResponse = await uploadFiles({ formData });
				file_id = uploadResponse[0]?._id || uploadResponse[0]?.id || null;
			}

			const { work_proof, ...rest } = values;
			const payload = { ...rest, file_id };

			if (isEditMode) {
				await dispatch(updateWorkOrder({ ...payload, _id: fullRecord?._id || dataToEdit._id }));
			} else {
				await dispatch(addNewWorkOrder(payload));
			}
		} catch (error) {
			console.error('Error al guardar obra:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleToggleModal}
			centered={true}
			size='lg'
			backdrop='static'
		>
			<ModalHeader toggle={handleToggleModal} className='bg-light p-3'>
				{isEditMode ? 'Editar Obra' : 'Agregar Nueva Obra'}
				<p className='text-muted mb-0' style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
					{isEditMode 
						? 'Actualiza los datos de la obra' 
						: 'Ingresa los datos requeridos para registrar una nueva obra'}
				</p>
			</ModalHeader>
			<ModalBody>
				{isLoadingRecord ? (
					<div className='d-flex justify-content-center align-items-center py-4'>
						<Spinner color='primary' />
					</div>
				) : (
					<WorkForm
						ref={formRef}
						onSubmit={handleSubmit}
						dataToEdit={isEditMode ? (fullRecord || null) : null}
						waterRightData={waterRightData}
					/>
				)}
			</ModalBody>
			<ModalFooter>
				<Button
                    type='button'
                    onClick={() => {
                        handleToggleModal();
                    }} className='btn-danger btn-label'>
                    <i className="ri-close-line label-icon"></i>
                    Cancelar
                </Button>
				<Button
                    color='primary'
                    className='btn btn-label btn-primary'
					onClick={() => {
						if (formRef.current) {
							formRef.current.handleSubmit();
						}
					}}
					disabled={isSubmitting || isAdding || isUpdating || isLoadingRecord}
				>
					{(isSubmitting || isAdding || isUpdating) ? (
						<i className='label-icon'><Spinner size='sm' /></i>
					) : (
						<i className='ri-save-line label-icon'></i>
					)}
					{isEditMode ? 'Actualizar Obra' : 'Guardar Obra'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default AddWorkModal;
